import { useEffect } from 'react'
import { isNil, omit } from 'lodash'
import { NumericFormat, PatternFormat } from 'react-number-format'
import CurrencyInput from 'react-currency-input-field'
import { E164Number } from 'libphonenumber-js'
import PhoneInput from 'react-phone-number-input/input'

import { useClearOnUnmount, useFieldHelpers } from '@/base'
import { Input, InputProps } from '../../atoms/input'
import { FormField, FormFieldProps } from '.'
import { cva } from 'class-variance-authority'
import { mtc } from '@/styles'

const inputStyles = cva(
  'w-full h-10 bg-transparent px-4 py-2 text-base shadow-sm transition-colors rounded-lg border border-neutral-200 placeholder:text-neutral-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-neutral-50 md:text-sm'
)

export const FormInput = (
  props: Omit<InputProps, 'onChange' | 'value' | 'defaultValue'> &
    Omit<FormFieldProps, 'children'> & {
      onChangeCallback?: (_?: string) => void
      shouldClearOnDismount?: boolean
      componentType?:
        | 'currency'
        | 'number'
        | 'phone'
        | 'pattern'
        | 'location'
        | 'date'
        | 'text'
        | 'currencyInCents'
      format?: string
      wrapperClassName?: string
      inputClassName?: string
      allowNegative?: boolean
      allowNegativeValue?: boolean
      onChange?: (value: string) => void
      onClearClick?: () => void
      onClick?: () => void
      onFocus?: () => void
      onLeftIconClick?: () => void
      onRightIconClick?: () => void
      allowLeadingZeros?: boolean
      errorTestId?: string
    }
) => {
  const {
    onChangeCallback,
    placeholder,
    name,
    label,
    schema,
    shouldClearOnDismount,
    componentType = 'text',
    format,
    onChange,
    onClick,
    allowLeadingZeros,
    wrapperClassName,
    afterLabel,
    errorTestId,
    inputClassName,
  } = props
  const { field, helpers } = useFieldHelpers({ name, schema })
  useClearOnUnmount(shouldClearOnDismount, helpers.setValue)

  useEffect(() => {
    if (onChangeCallback) {
      onChangeCallback(field.value)
    }
  }, [field.value, onChangeCallback])

  const placeholderOrLabel =
    placeholder || (typeof label === 'string' ? label : undefined)

  return (
    <FormField
      name={name}
      label={label}
      schema={schema}
      message={props.message}
      description={props.description}
      optionalText={props.optionalText}
      errorTestId={`error-${name}`}
      className={wrapperClassName}
      afterLabel={afterLabel}
    >
      <>
        {componentType === 'phone' && (
          <PhoneInput
            country="US"
            className={mtc(inputStyles(), inputClassName)}
            onChange={(value?: E164Number) => {
              helpers.setValue(value?.toString() ?? '')
            }}
            value={field.value || ''}
            required={props.required}
            {...omit(props, 'onChange', 'componentType')}
            {...omit(field, 'value', 'onChange')}
          />
        )}
        {componentType === 'number' && (
          <NumericFormat
            className="numberFormat"
            onValueChange={({ value, floatValue }) =>
              allowLeadingZeros
                ? onChange?.(value?.toString() || '')
                : onChange?.(floatValue?.toString() || '')
            }
            allowLeadingZeros={allowLeadingZeros}
            decimalScale={2}
            fixedDecimalScale
            displayType="input"
            thousandSeparator
            prefix="$"
            type="text"
            value={field.value || ''}
            {...omit(
              props,
              'onChange',
              'isDateInput',
              'type',
              'size',
              'tabIndex',
              'onValueChange',
              'customInput',
              'componentType'
            )}
            {...omit(field, 'value', 'onChange')}
          />
        )}
        {componentType === 'pattern' && (
          <PatternFormat
            className={mtc(inputStyles(), 'patternFormat')}
            onValueChange={({ formattedValue }) => {
              helpers.setValue(formattedValue)
              return onChange?.(formattedValue)
            }}
            displayType="input"
            type="text"
            placeholder={placeholderOrLabel}
            value={field.value || ''}
            {...omit(
              props,
              'onChange',
              'isDateInput',
              'type',
              'size',
              'tabIndex',
              'onValueChange',
              'customInput',
              'componentType'
            )}
            format={props.format || ''}
          />
        )}
        {componentType === 'currency' && (
          <div className="" data-testid={errorTestId}>
            <CurrencyInput
              className={mtc(inputStyles(), inputClassName)}
              prefix="$"
              value={isNil(field.value) ? '' : field.value}
              onValueChange={(_, __, values) => {
                // Currency input can return `09` as a value.  The 0 needs to be removed however for `0.` the 0 cannot be removed
                onChange?.(
                  values?.value === '0.'
                    ? values?.value
                    : values?.value.replace(/^0+/, '') || '0'
                )
                const cleaned =
                  values?.value === '0.'
                    ? values?.value
                    : values?.value?.replace(/^0+/, '') || '0'

                helpers.setValue(cleaned)
                onChange?.(cleaned)
              }}
              allowNegativeValue={
                props.allowNegative || props.allowNegativeValue
              }
              onBlur={(event) => {
                // On blur change the number to a formatted version
                if (!isNaN(Number(field.value))) {
                  helpers.setValue(Number(field.value).toFixed(2))
                }
                helpers.setTouched(true)
                props.onBlur?.(event)
              }}
              onClick={onClick}
              {...omit(
                props,
                'onChange',
                'isDateInput',
                'type',
                'size',
                'tabIndex',
                'step',
                'componentType'
              )}
            />
          </div>
        )}
        {componentType === 'currencyInCents' && (
          <div className="" data-testid={errorTestId}>
            <CurrencyInput
              className={mtc(inputStyles(), inputClassName)}
              prefix="$"
              value={
                isNil(field.value) ? '' : (Number(field.value) / 100).toFixed(2)
              }
              onValueChange={(_, __, values) => {
                const float = parseFloat(values?.float?.toString() ?? '0')
                const cents = Math.round(float * 100)
                helpers.setValue(cents)
                onChange?.(cents.toString())
              }}
              allowNegativeValue={
                props.allowNegative || props.allowNegativeValue
              }
              onBlur={(event) => {
                helpers.setTouched(true)
                props.onBlur?.(event)
              }}
              onClick={onClick}
              {...omit(
                props,
                'onChange',
                'isDateInput',
                'type',
                'size',
                'tabIndex',
                'step',
                'componentType'
              )}
            />
          </div>
        )}
        {!componentType ||
          componentType === 'date' ||
          componentType === 'location' ||
          (componentType === 'text' && (
            <Input
              placeholder={placeholderOrLabel}
              type={componentType}
              format={format}
              {...props}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => helpers.setValue(e.target.value)}
            />
          ))}
      </>
    </FormField>
  )
}
